/**
 * My WebsiteTech — the client-facing Revenue Chain dashboard.
 *
 * Visualises the Traffic → Leads → Qualified → Appointments → Customers →
 * Revenue funnel, per-channel attribution, and prioritised recommendations.
 * Backed by GET /api/my-websitetech/revenue-chain.
 */
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Users, Target, UserCheck, CalendarCheck, Crown, DollarSign,
  TrendingUp, Lightbulb, ArrowRight, RefreshCw, AlertTriangle,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: "visitors",     label: "Visitors",     Icon: Users,          tone: "bg-slate-100 text-slate-700" },
  { key: "leads",        label: "Leads",        Icon: Target,         tone: "bg-blue-100 text-blue-700" },
  { key: "qualified",    label: "Qualified",    Icon: UserCheck,      tone: "bg-indigo-100 text-indigo-700" },
  { key: "appointments", label: "Appointments", Icon: CalendarCheck,  tone: "bg-amber-100 text-amber-700" },
  { key: "customers",    label: "Customers",    Icon: Crown,          tone: "bg-emerald-100 text-emerald-700" },
  { key: "revenue",      label: "Revenue",      Icon: DollarSign,     tone: "bg-emerald-600 text-white" },
];

function fmt(n, currency) {
  if (n == null) return "—";
  if (currency) return `$${(n || 0).toLocaleString()}`;
  return (n || 0).toLocaleString();
}

export default function MyWebsiteTechDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  usePageSeo({
    title: "My WebsiteTech — Revenue Chain Dashboard | WebsiteTech.pro",
    description: "Your acquisition engine on a single screen — visitors, leads, qualified, appointments, customers and revenue by channel. See exactly which marketing drove revenue.",
    noindex: true,
  });
  useBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "My WebsiteTech", url: "/my-websitetech" },
  ]);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("wt_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data: r } = await axios.get(`${API}/my-websitetech/revenue-chain?rng=${range}`, { headers });
      setData(r);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) toast.error("Please log in to view your dashboard");
      else toast.error(e?.response?.data?.detail || "Could not load dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [range]);

  const maxFunnel = useMemo(() => {
    if (!data?.funnel) return 1;
    return Math.max(data.funnel.visitors || 1, data.funnel.leads || 0,
                    data.funnel.qualified || 0, data.funnel.appointments || 0,
                    data.funnel.customers || 0);
  }, [data]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="pt-28 pb-20">
          <div className="max-w-6xl mx-auto px-4 py-20 text-center text-slate-400">
            <Loader2 size={28} className="mx-auto animate-spin" />
            <p className="mt-3 text-sm">Loading your revenue chain…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="pt-28 pb-20" data-testid="my-wt-dashboard">
          <div className="max-w-3xl mx-auto px-4 py-20 text-center">
            <AlertTriangle size={36} className="mx-auto text-amber-500" />
            <h1 className="mt-4 font-heading text-3xl font-bold text-slate-900">Dashboard unavailable</h1>
            <p className="mt-2 text-slate-600">Log in to see your Traffic → Revenue chain.</p>
            <Link to="/login" className="mt-6 inline-flex btn-primary rounded-lg px-5 py-2.5 text-sm font-semibold">Log in</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { funnel, rates, by_source, headline_promise, recommendations, data_quality } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="pt-28 pb-20" data-testid="my-wt-dashboard">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">My WebsiteTech</p>
              <h1 className="mt-2 font-heading font-extrabold text-slate-900 text-4xl sm:text-5xl leading-tight">
                Your revenue chain<br />
                <span className="text-brand">on one screen.</span>
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  data-testid={`my-wt-range-${d}`}
                  onClick={() => setRange(d)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${range === d ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200"}`}
                >
                  {d}d
                </button>
              ))}
              <button
                data-testid="my-wt-refresh"
                onClick={load}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-brand"
              >
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
          </div>

          {/* Headline promise */}
          <div className="mt-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 lg:p-8" data-testid="my-wt-headline">
            <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-300 mb-3">The acquisition engine · {data.period.label}</div>
            <p className="text-lg md:text-xl leading-relaxed font-medium">{headline_promise}</p>
            {data_quality?.leads === "estimated" && (
              <p className="mt-4 text-[11px] text-amber-200 uppercase tracking-wider">
                * Visitors, revenue and some lead data are estimated. Connect analytics + CRM to replace with live numbers.
              </p>
            )}
          </div>

          {/* Funnel bars */}
          <section className="mt-8" data-testid="my-wt-funnel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-bold text-slate-900 text-xl">Traffic → Revenue chain</h2>
              <span className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Each row shrinks to the conversion rate</span>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
              {STAGES.map((stage) => {
                const v = funnel[stage.key];
                const pct = stage.key === "revenue"
                  ? 100
                  : Math.max(2, Math.round(((v || 0) / (maxFunnel || 1)) * 100));
                return (
                  <div key={stage.key} data-testid={`my-wt-stage-${stage.key}`} className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${stage.tone}`}>
                      <stage.Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">{stage.label}</span>
                        <span className="text-lg font-extrabold text-slate-900 tabular-nums">
                          {stage.key === "revenue" ? fmt(v, true) : fmt(v)}
                        </span>
                      </div>
                      <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${stage.key === "revenue" ? "bg-emerald-600" : stage.key === "customers" ? "bg-emerald-500" : stage.key === "appointments" ? "bg-amber-500" : stage.key === "qualified" ? "bg-indigo-500" : stage.key === "leads" ? "bg-blue-500" : "bg-slate-400"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Conversion rates row */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { k: "visitor_to_lead",          label: "Visitor → Lead" },
                { k: "lead_to_qualified",        label: "Lead → Qualified" },
                { k: "qualified_to_appointment", label: "Qualified → Appointment" },
                { k: "appointment_to_customer",  label: "Appointment → Customer" },
              ].map((r) => (
                <div key={r.k} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`my-wt-rate-${r.k}`}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{r.label}</div>
                  <div className="mt-1 text-2xl font-extrabold text-slate-900">{rates[r.k]}%</div>
                </div>
              ))}
            </div>
          </section>

          {/* Per-channel attribution */}
          <section className="mt-8" data-testid="my-wt-attribution">
            <h2 className="font-heading font-bold text-slate-900 text-xl">By channel — which marketing actually generated revenue</h2>
            <div className="mt-3 bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Channel</th>
                    <th className="text-right px-3 py-2 font-semibold">Visitors</th>
                    <th className="text-right px-3 py-2 font-semibold">Leads</th>
                    <th className="text-right px-3 py-2 font-semibold">Customers</th>
                    <th className="text-right px-3 py-2 font-semibold">Revenue</th>
                    <th className="text-right px-3 py-2 font-semibold">CPL</th>
                    <th className="text-right px-4 py-2 font-semibold">ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {by_source.map((s, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`my-wt-source-${s.source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <td className="px-4 py-2 font-semibold text-slate-800">{s.source}</td>
                      <td className="text-right px-3 py-2 tabular-nums">{fmt(s.visitors)}</td>
                      <td className="text-right px-3 py-2 tabular-nums">{fmt(s.leads)}</td>
                      <td className="text-right px-3 py-2 tabular-nums font-semibold text-emerald-700">{fmt(s.customers)}</td>
                      <td className="text-right px-3 py-2 tabular-nums font-bold">{fmt(s.revenue, true)}</td>
                      <td className="text-right px-3 py-2 tabular-nums text-slate-500">{s.cpl != null ? `$${s.cpl}` : "—"}</td>
                      <td className="text-right px-4 py-2 tabular-nums font-semibold">{s.roas != null ? `${s.roas}×` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <section className="mt-8" data-testid="my-wt-recommendations">
              <h2 className="font-heading font-bold text-slate-900 text-xl flex items-center gap-2">
                <Lightbulb size={18} className="text-amber-500" /> Next moves based on your chain
              </h2>
              <div className="mt-3 space-y-2">
                {recommendations.map((r, i) => (
                  <Link
                    key={i}
                    to={r.url}
                    data-testid={`my-wt-reco-${i}`}
                    className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-brand hover:shadow-sm transition-all"
                  >
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${r.priority === "high" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.priority}
                    </span>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900 text-sm">{r.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.detail}</div>
                    </div>
                    <ArrowRight size={14} className="text-slate-400" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Footer promise */}
          <p className="mt-10 text-xs text-slate-400">
            This dashboard is updated continuously as your websites, chatbots and campaigns run.
            Connect Google Analytics, Google Ads and your CRM in{" "}
            <Link to="/portal/connectors" className="underline text-brand">Portal → Connectors</Link>{" "}
            to replace estimated figures with live numbers.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
